// Messagerie (H2, charte D-038) : liste des conversations avec non lus (canal de crise en tête), filtres par
// type, archives, recherche plein texte limitée à ses propres conversations, nouvelle conversation et fil.
// Sur téléphone, la liste, le fil et la création s'affichent l'un après l'autre (parcours complet).

import { useMemo, useState, type FormEvent } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure } from '../../composants/format';
import { intervalleSecours, LIBELLES_ETAT_FLUX, useEtatFlux } from '../notifications/tempsReel';
import {
  cles,
  dateCourte,
  listerConversations,
  lireStatut,
  rechercher,
  segmentsExtrait,
  type ResumeConversation,
} from './api';
import { FilConversation } from './Fil';
import { IconeConversation, IconeH2 } from './icones';
import { NouvelleConversation } from './NouvelleConversation';
import '../notifications/notifications.css';
import './messagerie.css';

type Filtre = 'toutes' | 'non_lues' | 'crise' | 'service' | 'directes' | 'annonces' | 'archives';

const FILTRES_TYPES: { code: Filtre; libelle: string; garde: (c: ResumeConversation) => boolean }[] = [
  { code: 'crise', libelle: 'Crise', garde: (c) => c.type === 'crise' },
  { code: 'service', libelle: 'Service', garde: (c) => c.type === 'service' },
  {
    code: 'directes',
    libelle: 'Directes',
    garde: (c) => c.type === 'individuelle' || c.type === 'groupe',
  },
  { code: 'annonces', libelle: 'Annonces', garde: (c) => c.type === 'annonces' },
];

function ElementConversation({ c, actif }: { c: ResumeConversation; actif: boolean }) {
  const d = c.dernierMessage;
  const prefixe = !d
    ? ''
    : d.nature === 'decision'
      ? 'Décision consignée : '
      : d.nature === 'notification'
        ? 'Notification : '
        : d.auteur
          ? `${d.auteur} : `
          : '';
  return (
    <li>
      <Link
        to={`/messages/${c.id}`}
        className={`msg-element${actif ? ' actif' : ''}${c.nonLus > 0 ? ' non-lu' : ''}${c.type === 'crise' ? ' crise' : ''}`}
        aria-current={actif ? 'page' : undefined}
      >
        <span className="msg-picto" aria-hidden="true">
          <IconeConversation type={c.type} objetType={c.objetType} />
        </span>
        <span className="msg-element-texte">
          <span className="msg-element-haut">
            <b className="msg-element-titre">
              {c.type === 'individuelle' && c.interlocuteur ? c.interlocuteur : c.titre}
            </b>
            <time dateTime={c.derniereActiviteLe} title={dateHeure(c.derniereActiviteLe)}>
              {dateCourte(c.derniereActiviteLe)}
            </time>
          </span>
          <span className="msg-element-bas">
            {/* Le normal reste discret : étiquette pour le canal de crise, l'objet d'un canal de service et les
                annonces ; le type des conversations directes reste lisible par les lecteurs d'écran. */}
            {c.type === 'crise' || c.type === 'service' || c.type === 'annonces' ? (
              <span className={c.type === 'crise' ? 'etiquette rouge' : 'etiquette'}>
                {c.type === 'annonces' ? 'Annonce' : c.libelleType}
              </span>
            ) : (
              <span className="visuellement-cache">{c.libelleType}, </span>
            )}
            {d && (
              <span className="msg-element-extrait">
                {prefixe}
                {d.extrait}
              </span>
            )}
          </span>
        </span>
        {c.nonLus > 0 && (
          <span className="msg-non-lus">
            <span aria-hidden="true">{c.nonLus > 99 ? '99+' : c.nonLus}</span>
            <span className="visuellement-cache">
              {c.nonLus} message{c.nonLus > 1 ? 's' : ''} non lu{c.nonLus > 1 ? 's' : ''}
            </span>
          </span>
        )}
      </Link>
    </li>
  );
}

function Resultats({ q, effacer }: { q: string; effacer: () => void }) {
  const r = useQuery({ queryKey: ['messagerie', 'recherche', q], queryFn: () => rechercher(q) });
  return (
    <section className="msg-resultats" aria-label={`Résultats pour ${q}`}>
      <div className="msg-resultats-tete">
        <span className="sur-titre">Résultats pour « {q} »</span>
        <button type="button" className="bouton discret" onClick={effacer}>
          Effacer la recherche
        </button>
      </div>
      {r.isLoading && <Squelette lignes={3} libelle="Recherche en cours" />}
      {r.error && <MessageErreur erreur={r.error} titre="Recherche impossible" />}
      {r.data && r.data.length === 0 && (
        <p className="attenue msg-vide">Aucun message ne correspond dans vos conversations.</p>
      )}
      {r.data && r.data.length > 0 && (
        <ul className="msg-liste">
          {r.data.map((x) => (
            <li key={x.messageId}>
              <Link to={`/messages/${x.conversationId}?message=${x.messageId}`} className="msg-element">
                <span className="msg-picto" aria-hidden="true">
                  <IconeH2 nom="recherche" />
                </span>
                <span className="msg-element-texte">
                  <span className="msg-element-haut">
                    <b className="msg-element-titre">{x.conversationTitre}</b>
                    <time dateTime={x.creeLe}>{dateCourte(x.creeLe)}</time>
                  </span>
                  <span className="msg-element-extrait msg-extrait-recherche">
                    <span className="contexte">
                      {x.nature === 'decision'
                        ? 'Décision consignée'
                        : (x.auteur ?? 'Notification automatique')}{' '}
                      :{' '}
                    </span>
                    {segmentsExtrait(x.extrait).map((s, i) =>
                      s.marque ? <mark key={i}>{s.texte}</mark> : <span key={i}>{s.texte}</span>,
                    )}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Accès à la messagerie depuis la barre supérieure, avec le nombre de conversations non lues. */
export function AccesMessagerie() {
  const etat = useEtatFlux();
  const lieu = useLocation();
  const statut = useQuery({
    queryKey: cles.statut,
    queryFn: lireStatut,
    refetchInterval: intervalleSecours(etat),
    retry: false,
  });
  if (!statut.data?.disponible) return null;
  const n = statut.data.conversationsNonLues;
  const libelle =
    n > 0
      ? `Messages, ${n} conversation${n > 1 ? 's' : ''} non lue${n > 1 ? 's' : ''}`
      : 'Messages, tout est lu';
  return (
    <Link
      to="/messages"
      className="bouton msg-acces"
      aria-label={libelle}
      aria-current={lieu.pathname.startsWith('/messages') ? 'page' : undefined}
    >
      <Icone nom="message" />
      <span className="libelle-bouton">Messages</span>
      {n > 0 && (
        <span className="notif-compteur" aria-hidden="true">
          {n > 99 ? '99+' : n}
        </span>
      )}
    </Link>
  );
}

export function PageMessagerie({ nouvelle = false }: { nouvelle?: boolean }) {
  const { id } = useParams();
  const etatFlux = useEtatFlux();
  const [filtre, setFiltre] = useState<Filtre>('toutes');
  const [saisie, setSaisie] = useState('');
  const [q, setQ] = useState('');
  const archives = filtre === 'archives';
  const liste = useQuery({
    queryKey: cles.conversations(archives),
    queryFn: () => listerConversations(archives),
    refetchInterval: intervalleSecours(etatFlux),
  });
  const toutes = useMemo(() => liste.data?.donnees ?? [], [liste.data]);
  const totalNonLus = (liste.data?.meta.nonLus as number | undefined) ?? 0;
  const conversationsNonLues = toutes.filter((c) => c.nonLus > 0).length;
  const visibles = useMemo(() => {
    if (filtre === 'non_lues') return toutes.filter((c) => c.nonLus > 0);
    const f = FILTRES_TYPES.find((x) => x.code === filtre);
    return f ? toutes.filter(f.garde) : toutes;
  }, [toutes, filtre]);
  // Exceptions d'abord : le canal de crise est épinglé en tête de la liste complète.
  const crises = filtre === 'toutes' ? visibles.filter((c) => c.type === 'crise') : [];
  const autres = filtre === 'toutes' ? visibles.filter((c) => c.type !== 'crise') : visibles;
  const ouvert = Boolean(id) || nouvelle;

  const rechercherSoumis = (e: FormEvent) => {
    e.preventDefault();
    setQ(saisie.trim().length >= 2 ? saisie.trim() : '');
  };

  return (
    <div className={`msg-disposition${ouvert ? ' avec-fil' : ''}`}>
      <aside className="msg-colonne-liste" aria-label="Conversations">
        <div className="msg-liste-tete">
          <div>
            <h1>Messages</h1>
            <p className="contexte">
              {archives
                ? 'Conversations archivées après 12 mois sans activité'
                : conversationsNonLues > 0
                  ? `${totalNonLus} message${totalNonLus > 1 ? 's' : ''} non lu${totalNonLus > 1 ? 's' : ''} dans ${conversationsNonLues} conversation${conversationsNonLues > 1 ? 's' : ''}`
                  : 'Tout est lu'}
            </p>
          </div>
          <Link
            to="/messages/nouvelle"
            className="bouton primaire"
            aria-current={nouvelle ? 'page' : undefined}
          >
            <Icone nom="ajout" />
            <span>Nouvelle</span>
            <span className="visuellement-cache">conversation</span>
          </Link>
        </div>
        <form role="search" className="msg-champ-recherche" onSubmit={rechercherSoumis}>
          <IconeH2 nom="recherche" />
          <label htmlFor="msg-recherche" className="visuellement-cache">
            Rechercher dans vos conversations
          </label>
          <input
            id="msg-recherche"
            type="search"
            placeholder="Mot ou expression"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            minLength={2}
          />
          <button type="submit" className="bouton discret">
            Rechercher
          </button>
        </form>
        {q ? (
          <Resultats
            q={q}
            effacer={() => {
              setQ('');
              setSaisie('');
            }}
          />
        ) : (
          <>
            <div className="puces msg-filtres" role="group" aria-label="Filtrer les conversations">
              <button
                type="button"
                className="puce"
                aria-pressed={filtre === 'toutes'}
                onClick={() => setFiltre('toutes')}
              >
                Toutes
              </button>
              <button
                type="button"
                className="puce"
                aria-pressed={filtre === 'non_lues'}
                onClick={() => setFiltre('non_lues')}
              >
                Non lues <span className="compte">{conversationsNonLues}</span>
              </button>
              {!archives &&
                FILTRES_TYPES.filter((f) => toutes.some(f.garde)).map((f) => (
                  <button
                    key={f.code}
                    type="button"
                    className="puce"
                    aria-pressed={filtre === f.code}
                    onClick={() => setFiltre(f.code)}
                  >
                    {f.libelle}
                  </button>
                ))}
              <button
                type="button"
                className="puce"
                aria-pressed={archives}
                onClick={() => setFiltre(archives ? 'toutes' : 'archives')}
              >
                Archives
              </button>
            </div>
            {liste.isLoading && <Squelette lignes={5} libelle="Chargement des conversations" />}
            {liste.error && <MessageErreur erreur={liste.error} titre="Messagerie indisponible" />}
            {liste.data && visibles.length === 0 && (
              <p className="attenue msg-vide">
                {archives
                  ? 'Aucune conversation archivée. Une conversation est archivée après 12 mois sans activité.'
                  : filtre === 'non_lues'
                    ? 'Aucune conversation non lue.'
                    : 'Aucune conversation pour l’instant.'}
              </p>
            )}
            {crises.length > 0 && (
              <section className="msg-groupe" aria-labelledby="groupe-crise">
                <h2 id="groupe-crise" className="sur-titre">
                  Canal de crise
                </h2>
                <ul className="msg-liste">
                  {crises.map((c) => (
                    <ElementConversation key={c.id} c={c} actif={c.id === id} />
                  ))}
                </ul>
              </section>
            )}
            {autres.length > 0 && (
              <section
                className="msg-groupe"
                aria-labelledby={crises.length > 0 ? 'groupe-conversations' : undefined}
              >
                {crises.length > 0 && (
                  <h2 id="groupe-conversations" className="sur-titre">
                    Conversations
                  </h2>
                )}
                <ul className="msg-liste">
                  {autres.map((c) => (
                    <ElementConversation key={c.id} c={c} actif={c.id === id} />
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
        <p
          className={`msg-etat-flux point-etat${etatFlux === 'connecte' ? ' vert' : etatFlux === 'interrompu' ? ' ambre' : ''}`}
          role="status"
        >
          {LIBELLES_ETAT_FLUX[etatFlux]}
        </p>
      </aside>
      <section
        className="msg-colonne-fil"
        aria-label={nouvelle ? 'Nouvelle conversation' : 'Fil de la conversation'}
      >
        {nouvelle ? (
          <NouvelleConversation />
        ) : id ? (
          <FilConversation key={id} id={id} />
        ) : (
          <div className="etat-vide msg-invite">
            <span className="pictogramme">
              <Icone nom="message" taille={28} />
            </span>
            <h2>
              {conversationsNonLues > 0
                ? `${conversationsNonLues} conversation${conversationsNonLues > 1 ? 's' : ''} à lire`
                : 'Choisissez une conversation'}
            </h2>
            <p>
              Les canaux de service s’ouvrent avec les livraisons, alertes, inspections, incidents,
              rapprochements et cargaisons ; le canal de crise réunit les membres d’une cellule activée.
            </p>
            <div className="actions">
              <Link to="/messages/nouvelle" className="bouton">
                <Icone nom="ajout" />
                Nouvelle conversation
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
